import type {
    PatientDetailDto,
    SeriesDto,
} from '~/backend/connector-next-api/connector-next-schemas';
import type { WorkflowStepId } from '~/containers/viewer/explorer-sidebar/WorkflowStepper/workflow-steps';
import {
    flattenSeries,
    imageContouredOn,
    isImageSeries,
    resolveCourseStage,
} from '~/containers/patient-list/model';
import type { CourseStage } from '~/containers/patient-list/model';

/**
 * What the user asked for when they pressed Open. Both parts are optional: a
 * plain Open states neither and lets the course stage and the primaries speak.
 */
export type OpenIntent = {
    workflow?: WorkflowStepId;
    /** Connector dataset ids of the objects the open was invoked on. */
    target?: { image?: string; structureSet?: string };
};

/**
 * The settled part of a resolution: what to land in and what to select, all of
 * it surrogate ids. This is the only part that crosses to the viewer — an
 * ambiguity is the list's business, and its candidates are whole series
 * carrying DICOM UIDs, which are PHI and have no reason to travel.
 */
export type OpenSelection = {
    /** Null only with `needsChoice`: a course in Received names no workflow. */
    workflow: WorkflowStepId | null;
    imageId?: string;
    /** Registration only: the other side of the pair. */
    secondaryImageId?: string;
    structureSetId?: string;
};

/** Objects an open resolved to, by connector dataset id. */
export type OpenResolution = OpenSelection & {
    /**
     * Several structure sets could fill the role and no primary settles it.
     * The user picks; the application never picks for them.
     */
    ambiguity?: { candidates: SeriesDto[] };
    /**
     * The course names no workflow, so there is nothing to open *into* yet.
     * The control asks rather than choosing. The objects are still resolved:
     * which workflow is unknown, which image and set the user means is not.
     */
    needsChoice?: boolean;
    /**
     * The open cannot proceed, and this is why. Nothing is selected with it:
     * a refusal that still named objects would invite a caller to open
     * anyway, which is the doomed pair this exists to prevent.
     *
     * Stays on the list, like `ambiguity` — the viewer is never asked to
     * carry out an open that was already refused.
     */
    unavailable?: OpenRefusal;
};

/** Why an open was refused. */
export type OpenRefusal =
    /**
     * A structure set was named whose image is not among the patient's
     * datasets — it names none, or names one the patient does not have.
     * There is nothing to draw it on: the viewer takes the image FROM the
     * set, so opening would end in an error about the dataset the user
     * chose. Said before the click rather than after it.
     */
    | 'structureSetHasNoImage'
    /**
     * The dataset the open named is not in the detail fetched for it, or is
     * there as another kind. Deleted between the click and the fetch, or a
     * row that never addressed an openable dataset.
     *
     * Refused rather than substituted. The whole point of addressing an open
     * by a Connector-issued id is that it cannot land on a different dataset;
     * falling through to the primary or the newest would do exactly that,
     * silently, for a user who pointed at something specific.
     */
    | 'targetIsGone';

/**
 * The workflow a course stage leads to.
 *
 * `Received` deliberately maps to nothing: data has arrived and no workflow has
 * been initiated, image preparation is usually unnecessary, and defaulting
 * everyone into it would make the first move of every course a wrong one the
 * user has to undo.
 */
const STAGE_WORKFLOW: Record<CourseStage, WorkflowStepId | null> = {
    Received: null,
    ImagePreparation: 'setup',
    ImageRegistration: 'registration',
    OarContouring: 'contouring',
    TargetContouring: 'contouring',
    DosePrediction: 'predictive-planning',
    Review: 'review',
    Exported: 'export',
};

/**
 * The workflow a course stage leads to, or null where it names none. Exported
 * so the list can offer that workflow's action first without re-deriving the
 * mapping — one table, read by both.
 */
export function stageWorkflowFor(
    stage: string | null | undefined
): WorkflowStepId | null {
    return STAGE_WORKFLOW[resolveCourseStage(stage)];
}

/** Workflows that open on a structure set as well as an image. */
const STRUCTURE_SET_WORKFLOWS: ReadonlySet<WorkflowStepId> = new Set([
    'contouring',
    'review',
    'predictive-planning',
    'export',
]);

const isStructureSet = (series: SeriesDto): boolean =>
    series.datasetType === 'StructureSet';

/**
 * Whether a structure set is contoured on the image being opened.
 *
 * No image — the patient has none, or the one resolved carries no Series
 * Instance UID — means no set is on it, so none is chosen. There is no pair
 * to open, and the honest move is to open nothing rather than name a set the
 * viewer would then have to complain about. The set is still retrieved and
 * still listed in Setup, where its properties are worth reading.
 *
 * A set the user NAMED is different: that is refused out loud, above.
 */
const isOnImage = (set: SeriesDto, image: SeriesDto | undefined): boolean =>
    image?.seriesInstanceUid !== undefined &&
    set.structureSet?.referencedImageSeriesUid === image.seriesInstanceUid;

const isApproved = (series: SeriesDto): boolean =>
    series.structureSet?.approvalStatus === 'Approved';

/** Series date as a sortable number; an absent or unparsable one sorts oldest. */
function dateValue(series: SeriesDto): number {
    const parsed = series.seriesDateTime
        ? Date.parse(series.seriesDateTime)
        : Number.NaN;
    return Number.isFinite(parsed) ? parsed : 0;
}

/** The newest of `candidates` by series date, ties broken by detail order. */
function newest(candidates: readonly SeriesDto[]): SeriesDto | undefined {
    return candidates.reduce<SeriesDto | undefined>(
        (best, candidate) =>
            best === undefined || dateValue(candidate) > dateValue(best)
                ? candidate
                : best,
        undefined
    );
}

/**
 * Resolves what an Open should put on screen, from what the user asked for,
 * what the patient's primaries say, and where the course is.
 *
 * Pure, and deliberately the only place these rules live: the worklist shows
 * the user what this returns *before* anything is retrieved, and hands the
 * result to the viewer rather than letting it derive its own — a second
 * derivation is how the prototype landed in Contouring while the list had
 * promised Review.
 */
export function resolveOpenIntent(
    detail: PatientDetailDto | undefined,
    intent: OpenIntent = {}
): OpenResolution {
    const series = flattenSeries(detail);
    const byId = new Map(
        series.filter((s) => s.id).map((s) => [s.id as string, s])
    );

    const workflow = intent.workflow ?? stageWorkflowFor(detail?.courseStage);
    // A course in Received names no workflow, so there is nothing to open
    // *into* — but the objects still resolve. The primaries are the clearest
    // statement the user can make about which image and which set matter, and
    // a plain Open on an untouched course is exactly where honouring them
    // counts: without this the viewer's own heuristic picks, and the
    // designation the user just made does nothing.
    const needsChoice = workflow === null;

    const images = series.filter(isImageSeries);

    // A named dataset must resolve to one of this patient's, as the kind it
    // was named as. `isImage` excludes RT objects, so a dose or registration
    // row cannot arrive here as "the image" and be opened as one.
    const targetSet = intent.target?.structureSet
        ? byId.get(intent.target.structureSet)
        : undefined;
    const targetImage = intent.target?.image
        ? byId.get(intent.target.image)
        : undefined;
    if (
        (intent.target?.structureSet !== undefined &&
            (targetSet === undefined || !isStructureSet(targetSet))) ||
        (intent.target?.image !== undefined &&
            (targetImage === undefined || !isImageSeries(targetImage)))
    ) {
        return { workflow, unavailable: 'targetIsGone' };
    }

    const primaryImage = detail?.primaryImageId
        ? byId.get(detail.primaryImageId)
        : undefined;

    // The image role: the target set's image, else the target image, else the
    // patient's primary, else the newest image. Only the last is a guess, and
    // it is the one the viewer would otherwise make on its own.
    const setsImage =
        targetSet !== undefined
            ? imageContouredOn(images, targetSet)
            : undefined;
    if (targetSet !== undefined && setsImage === undefined) {
        // The user named this set, so no other object is substituted for it
        // and no image is offered in its place: the honest answer is that it
        // cannot be shown, and the control that offered it says so.
        return { workflow, unavailable: 'structureSetHasNoImage' };
    }
    if (workflow === 'registration') {
        // The pivot ignores the target on purpose. Register invoked on the MR
        // row means "register this against the planning image", never "make
        // the MR primary" — so the named image is the other side, and the
        // pivot stays whatever the patient's primary says.
        const pivot = primaryImage ?? newest(images);
        return {
            workflow,
            imageId: pivot?.id,
            secondaryImageId: secondaryFor(images, pivot, targetImage)?.id,
        };
    }

    const image = setsImage ?? targetImage ?? primaryImage ?? newest(images);

    // Naming an image is the whole instruction. The user picked a row and
    // said what to do with it; adding a structure set they did not ask for
    // puts contours on screen over an image they chose deliberately — a
    // fresh reformat, a second series — and the set that would be added is
    // whichever the defaults happen to prefer. If they want one, they click
    // it: that row is right there, and it opens on its own image.
    if (targetImage !== undefined) {
        return { workflow, imageId: image?.id };
    }

    // A workflow that shows no structure set resolves none. An unnamed
    // workflow does resolve one: it will become one of these, and the set to
    // show is the same for every workflow but export.
    if (workflow !== null && !STRUCTURE_SET_WORKFLOWS.has(workflow)) {
        return { workflow, imageId: image?.id };
    }

    const structureSet = resolveStructureSet({
        series,
        image,
        targetSet,
        primary: detail?.primaryStructureSetId
            ? byId.get(detail.primaryStructureSetId)
            : undefined,
        // Export opens what is finished; every other workflow works on what is
        // not, so only export considers approved sets a default.
        unapprovedOnly: workflow !== 'export',
    });

    return {
        workflow,
        imageId: image?.id,
        ...structureSet,
        ...(needsChoice ? { needsChoice } : {}),
    };
}

/**
 * The other side of a registration pair: the target image when one was named,
 * otherwise the first image on a different frame of reference.
 *
 * The primary planning image is the pivot every registration is expressed
 * against, so it is the one role that never moves. Which direction the
 * transform is stored in is the registration step's problem, not this one's.
 */
function secondaryFor(
    images: readonly SeriesDto[],
    pivot: SeriesDto | undefined,
    targetImage: SeriesDto | undefined
): SeriesDto | undefined {
    if (targetImage !== undefined && targetImage.id !== pivot?.id) {
        return targetImage;
    }
    return images.find(
        (image) =>
            image.id !== pivot?.id &&
            !!image.frameOfReferenceUid &&
            image.frameOfReferenceUid !== pivot?.frameOfReferenceUid
    );
}

/**
 * The structure-set role: the target, else the patient's primary, else the
 * default rule — unapproved, newest, contoured on the image being opened.
 *
 * More than one candidate with no primary to settle it is reported as an
 * ambiguity rather than resolved. Picking the newest of several would be a
 * guess about which set the user meant, made silently, at the moment they can
 * least afford it.
 */
function resolveStructureSet({
    series,
    image,
    targetSet,
    primary,
    unapprovedOnly,
}: {
    series: readonly SeriesDto[];
    image: SeriesDto | undefined;
    targetSet: SeriesDto | undefined;
    primary: SeriesDto | undefined;
    unapprovedOnly: boolean;
}): Pick<OpenResolution, 'structureSetId' | 'ambiguity'> {
    if (targetSet !== undefined) {
        return { structureSetId: targetSet.id };
    }
    // The primary set is the default FOR THE IMAGE IT IS DRAWN ON. Opening a
    // different image — a reformat, a second series — must not pull it in:
    // the pair would name an image the set does not belong to, and the viewer
    // resolves that by taking the set's own image, landing the user somewhere
    // they did not ask for. Where the set's image was not retrieved at all it
    // cannot even do that, and the open ends in an error about a structure
    // set the user never named.
    if (
        primary !== undefined &&
        isStructureSet(primary) &&
        isOnImage(primary, image)
    ) {
        return { structureSetId: primary.id };
    }

    const onThisImage = series.filter(
        (s) => isStructureSet(s) && isOnImage(s, image)
    );
    const candidates = unapprovedOnly
        ? onThisImage.filter((s) => !isApproved(s))
        : onThisImage;

    if (candidates.length > 1) {
        return { ambiguity: { candidates } };
    }
    return { structureSetId: candidates[0]?.id };
}

/** The part of a resolution the viewer acts on, with nothing else attached. */
export function openSelection({
    workflow,
    imageId,
    secondaryImageId,
    structureSetId,
}: OpenResolution): OpenSelection {
    return { workflow, imageId, secondaryImageId, structureSetId };
}
